/**
 * App-list transport.
 *
 * Three contract facts the screens are built on:
 *
 *   • The list is the whole catalogue every time, not only what is installed.
 *     An app a company does not have is exactly what somebody opening this
 *     screen came to look at.
 *   • Refusals name the app they are about, in the problem's `module` member —
 *     "install Inventory first" is something an administrator can act on, and
 *     "conflict" is not.
 *   • Removing an app deletes nothing. The server keeps every row; the app
 *     simply stops answering.
 */
import type { components } from "@erp/api-client";

import { apiFetchOperational } from "@/lib/api/http";
import type { AppCode } from "@/lib/modules";

type Schemas = components["schemas"];

export type AppRecord = Schemas["Module"];
export type AppStatus = Schemas["ModuleStatus"];

export function listApps(signal?: AbortSignal): Promise<{ items: AppRecord[] }> {
  return apiFetchOperational<{ items: AppRecord[] }>("/modules", { signal });
}

/**
 * `expectedVersion` is the version the screen last saw. Sending it turns a
 * simultaneous change by another administrator into a refusal instead of a
 * silent overwrite; omitting it acts on whatever the current state is.
 */
export function installApp(code: AppCode, expectedVersion?: number): Promise<AppRecord> {
  return apiFetchOperational<AppRecord>(`/modules/${encodeURIComponent(code)}/install`, {
    method: "POST",
    body: expectedVersion === undefined ? {} : { expected_version: expectedVersion },
  });
}

export function uninstallApp(code: AppCode, expectedVersion?: number): Promise<AppRecord> {
  return apiFetchOperational<AppRecord>(`/modules/${encodeURIComponent(code)}/uninstall`, {
    method: "POST",
    body: expectedVersion === undefined ? {} : { expected_version: expectedVersion },
  });
}

/** The permissions that gate this screen. Reading is not managing. */
export const APP_READ_PERMISSION = "platform.module.read";
export const APP_MANAGE_PERMISSION = "platform.module.manage";

/**
 * The refusals these screens must explain rather than swallow. Each names a
 * rule the server states, and each has an answer somebody can act on.
 */
export const APP_REFUSALS = [
  "company_module.dependency_not_installed",
  "company_module.dependent_module_installed",
  "company_module.not_removable",
  "company_module.already_installed",
  "company_module.not_installed",
  "company_module.precondition_failed",
  "company_module.unknown_module",
] as const;
